<template>
    <b-modal
        v-model="shown"
        centered
        :title="title"
        lazy
    >
        <Spinner v-if="loading" />

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="description"
                > {{ $t('cost.description') }}
            </label>
        </div>
        <div
            v-if="fields.includes('description')"
            class="well"
        >
            <b-form-group
                class="mb-0 w-100 disabled"
            >
                <Editor
                    ref="toastuiEditor"
                    :options="editorConfig"
                    initial-edit-type="wysiwyg"
                    preview-style="tab"
                    @change="onDescriptionChange"
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="cost_type_id"
                > {{ $t('cost.type') }}
            </label>
        </div>
        <div
            v-if="fields.includes('cost_type_id')"
            class="well"
        >
            <b-form-group
                class="mb-0 w-100 disabled"
            >
                <Select
                    v-model="changes.cost_type_id"
                    placeholder="Select a type..."
                    action="selects/getCostTypes"
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="tax_rate_id"
                > {{ $t('cost.tax_rate') }}
            </label>
        </div>
        <div
            v-if="fields.includes('tax_rate_id')"
            class="well"
        >
            <b-form-group class="mb-0 w-100">
                <Select
                    v-model="changes.tax_rate_id"
                    placeholder="Select a tax rate..."
                    action="selects/getTaxRates"
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="supplier_account_id"
                > {{ $t('cost.supplier') }}
            </label>
        </div>
        <div
            v-if="fields.includes('supplier_account_id')"
            class="well"
        >
            <b-form-group
                class="mb-0 w-100 disabled"
            >
                <AccountSearchSelect
                    v-model="changes.supplier_account_id"
                    placeholder="Search for a supplier..."
                    tag-placeholder="Add as new supplier..."
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="quantity"
                > {{ $t('cost.quantity') }}
            </label>
        </div>
        <div
            v-if="fields.includes('quantity')"
            class="well"
        >
            <QuantityAndUnitInputGroup
                v-model="changes.quantity"
            />
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="unit_cost"
                > {{ $t('cost.unit_cost') }}
            </label>
        </div>
        <div
            v-if="fields.includes('unit_cost')"
            class="well"
        >
            <b-form-group class="mb-0 w-100">
                <b-form-input
                    v-model="changes.unit_cost"
                    type="text"
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="unit_price"
                > {{ $t('cost.unit_price') }}
            </label>
        </div>
        <div
            v-if="fields.includes('unit_price')"
            class="well"
        >
            <b-form-group class="mb-0 w-100">
                <b-form-input
                    v-model="changes.unit_price"
                    type="text"
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="discount"
                > {{ $t('cost.discount') }}
            </label>
        </div>
        <div
            v-if="fields.includes('discount')"
            class="well"
        >
            <b-form-group class="mb-0 w-100">
                <AbsoluteOrRelativeCurrencyToggleInputGroup
                    input-label="Discount"
                    input-placeholder="Enter discount..."
                    input-validation-required-message="Please enter a discount amount."
                    checkbox-label="Fixed"
                    checkbox-hint="Use a fixed discount value (e.g. £100) instead of a percentage of the unit cost/price."
                    @value="(value) => (changes.discount) = value"
                    @fixed="(isAbsolute) => (changes.is_fixed_discount) = isAbsolute"
                />
            </b-form-group>
        </div>
        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="duration"
                > {{ $t('cost.duration') }}
            </label>
        </div>
        <div
            v-if="fields.includes('duration')"
            class="well"
        >
            <b-form-checkbox v-model="changes.duration_enabled">
                Add duration
            </b-form-checkbox>
            <!--/ Add Duration Checkbox -->

            <div v-if="changes.duration_enabled">
                <!-- Enter Times Checkbox -->
                <b-form-checkbox
                    v-model="changes.duration_time_enabled"
                    class="mb-2"
                >
                    Enter start and finish times
                </b-form-checkbox>
                <!--/ Enter Times Checkbox -->
                <!-- Time Input -->
                <b-form-group v-if="changes.duration_time_enabled">
                    <div class="d-flex">
                        <b-input-group
                            prepend="Start"
                            class="mr-2"
                        >
                            <b-input
                                v-model="changes.duration_time_start"
                                type="time"
                            />
                        </b-input-group>
                        <b-input-group prepend="End">
                            <b-input
                                v-model="changes.duration_time_end"
                                type="time"
                            />
                        </b-input-group>
                    </div>
                </b-form-group>
                <!--/ Time Input -->

                <!-- Number Input -->
                <QuantityAndUnitInputGroup
                    v-else
                    v-model="changes.duration_quantity"
                />
                <!--/ Number Input -->
            </div>

        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="commission"
                > {{ $t('cost.commission') }}
            </label>
        </div>
        <div
            v-if="fields.includes('commission')"
            class="well"
        >
            <b-form-group class="mb-0 w-100">
                <AbsoluteOrRelativeCurrencyToggleInputGroup
                    input-label="Commission"
                    input-placeholder="Enter commission..."
                    input-validation-required-message="Please enter a commission amount."
                    checkbox-label="Fixed"
                    checkbox-hint="Use a fixed commission value (e.g. £100) instead of a percentage of the unit cost/price."
                    @value="(value) => (changes.commission) = value"
                    @fixed="(isAbsolute) => (changes.is_fixed_commission) = isAbsolute"
                />
                <b-form-radio-group v-model="changes.commission_type">
                    <b-form-radio value="receivable">
                        Receivable
                    </b-form-radio>
                    <b-form-radio value="payable">
                        Payable
                    </b-form-radio>
                </b-form-radio-group>
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="management_fee"
                > {{ $t('cost.fee') }}
            </label>
        </div>
        <div
            v-if="fields.includes('management_fee')"
            class="well"
        >
            <div class="well">
                <b-form-group class="mb-0 w-100">
                    <b-input-group>
                        <b-input-group-text slot="prepend">
                            %
                        </b-input-group-text>
                        <b-form-input v-model="changes.management_fee" />
                    </b-input-group>
                </b-form-group>
            </div>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="user_id"
                > {{ $t('cost.user') }}
            </label>
        </div>
        <div
            v-if="fields.includes('user_id')"
            class="well"
        >
            <b-form-group class="mb-0 w-100">
                <UserSelect
                    v-model="changes.user_id"
                    placeholder="Select a user..."
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="cost_status_id"
                > {{ $t('cost.status') }}
            </label>
        </div>
        <div
            v-if="fields.includes('cost_status_id')"
            class="well"
        >
            <b-form-group
                class="mb-0 w-100 disabled"
            >
                <Select
                    v-model="changes.cost_status_id"
                    placeholder="Select a status..."
                    action="selects/getCostStatuses"
                />
            </b-form-group>
        </div>

        <div
            v-if="!$store.getters['costable/settings'].hide_cost_statuses && $store.getters['costable/settings'].show_secondary_cost_status"
            class="checkbox"
        >
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="secondary_status_id"
                > {{ $t('cost.secondary_status') }}
            </label>
        </div>
        <div
            v-if="fields.includes('secondary_status_id')"
            class="well"
        >
            <b-form-group
                class="mb-0 w-100 disabled"
            >
                <Select
                    v-model="changes.secondary_status_id"
                    placeholder="Select a status..."
                    action="selects/getSecondaryCostStatuses"
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="is_billable"
                > {{ $t('cost.is_billable') }}
            </label>
        </div>
        <div
            v-if="fields.includes('is_billable')"
            class="well"
        >
            <b-form-group>
                <b-form-radio-group
                    id="btn-radios-1"
                    v-model="changes.is_billable"
                    :options="[{text: 'Yes', value: true}, {text: 'No', value: false}]"
                    button-variant="outline-primary"
                    name="radio-btn-outline"
                    buttons
                />
            </b-form-group>
        </div>

        <div class="checkbox">
            <label>
                <input
                    v-model="fields"
                    type="checkbox"
                    value="is_vatable"
                > {{ $t('cost.is_vatable') }}
            </label>
        </div>
        <div
            v-if="fields.includes('is_vatable')"
            class="well"
        >
            <b-form-group>
                <b-form-radio-group
                    id="btn-radios-1"
                    v-model="changes.is_vatable"
                    :options="[{text: 'Yes', value: true}, {text: 'No', value: false}]"
                    button-variant="outline-primary"
                    name="radio-btn-outline"
                    buttons
                />
            </b-form-group>
        </div>

        <div slot="modal-footer">
            <b-btn
                variant="default"
                class="mr-2"
                @click="handleClose"
            >
                Close
            </b-btn>
            <Button
                variant="success"
                :disabled="loading"
                :loading="submitLoading"
                @click="handleSubmit"
            >
                Save
            </Button>
        </div>
    </b-modal>
</template>

<script>
import vueComponentReset from '@ianwalter/vue-component-reset';
import { Editor } from '@toast-ui/vue-editor';
import numeral from 'numeral';
import AccountSearchSelect from '@/components/AccountSearchSelect';
import UserSelect from '@/components/UserSelect';
import QuantityAndUnitInputGroup from '../../../components/QuantityAndUnitInputGroup';

const resetMixin = vueComponentReset(() => ({
    shown: false,
    loading: false,
    submitLoading: false,
    changes: {},
    fields: [],
    editorConfig: {
        hideModeSwitch: true,
        usageStatistics: false,
    },
}));

export default {

    components: {
        QuantityAndUnitInputGroup,
        AccountSearchSelect,
        Editor,
        UserSelect,
    },

    mixins: [
        resetMixin,
    ],

    computed: {
        selectedCostIds() {
            return this.$store.getters['costable/selectedCostIds'];
        },
        title() {
            return `Edit ${this.selectedCostIds.length} costs`;
        },
        filteredChanges() {
            let filteredChanges = {};

            if (this.fields.includes('quantity')) {
                filteredChanges = {
                    ...filteredChanges,
                    original_quantity: this.changes.quantity.value,
                    quantity_unit: this.changes.quantity.unit,
                };
            }

            if (this.fields.includes('unit_cost')) {
                filteredChanges.unit_cost = this.changes.unit_cost;
            }

            if (this.fields.includes('unit_price')) {
                filteredChanges.unit_price = this.changes.unit_price;
            }

            if (this.fields.includes('discount')) {
                filteredChanges = {
                    ...filteredChanges,
                    discount: this.changes.discount,
                    is_fixed_discount: this.changes.is_fixed_discount,
                };
            }

            if (this.fields.includes('duration')) {
                filteredChanges = {
                    ...filteredChanges,
                    display_duration: this.changes.duration_enabled ?? false,
                    duration_enabled: this.changes.duration_enabled ?? false,
                    has_time_based_duration: this.changes.duration_time_enabled ?? false,
                    start_time: this.changes.duration_time_start,
                    end_time: this.changes.duration_time_end,
                    duration: this.changes.duration_quantity ? numeral(this.changes.duration_quantity.value).value() : null,
                    duration_unit: this.changes.duration_quantity ? this.changes.duration_quantity.unit : null,
                };
            }

            if (this.fields.includes('commission')) {
                filteredChanges = {
                    ...filteredChanges,
                    commission: this.changes.commission,
                    is_fixed_commission: this.changes.is_fixed_commission,
                    commission_type: this.changes.commission_type,
                };
            }

            if (this.fields.includes('cost_status_id')) {
                filteredChanges.cost_status_id = this.changes.cost_status_id.id;
            }

            if (this.fields.includes('secondary_status_id')) {
                filteredChanges.secondary_status_id = this.changes.secondary_status_id.id;
            }

            if (this.fields.includes('cost_type_id')) {
                filteredChanges.cost_type_id = this.changes.cost_type_id.id;
            }

            if (this.fields.includes('supplier_account_id')) {
                filteredChanges.supplier_account_id = this.changes.supplier_account_id.id;
            }

            if (this.fields.includes('description')) {
                filteredChanges.description = this.changes.description;
            }

            if (this.fields.includes('management_fee')) {
                filteredChanges.management_fee = this.changes.management_fee;
            }

            if (this.fields.includes('user_id')) {
                filteredChanges.user_id = this.changes.user_id.id;
            }

            if (this.fields.includes('user_id')) {
                filteredChanges.user_id = this.changes.user_id.id;
            }

            if (this.fields.includes('tax_rate_id')) {
                filteredChanges.tax_rate_id = this.changes.tax_rate_id.id;
            }

            if (this.fields.includes('is_billable')) {
                filteredChanges.is_billable = !!this.changes.is_billable;
            }

            if (this.fields.includes('is_vatable')) {
                filteredChanges.is_vatable = !!this.changes.is_vatable;
            }

            return filteredChanges;
        },
    },

    created() {
        this.$eventBus.$on(this.$events.openChangeMultipleModal, () => {
            this.reset();
            this.shown = true;
        });
    },

    methods: {
        async handleSubmit() {
            this.submitLoading = true;
            await this.$store.dispatch('costable/patchCosts', {
                costIds: this.selectedCostIds,
                data: this.filteredChanges,
            });
            this.submitLoading = false;
            this.handleClose();
        },
        handleClose() {
            this.shown = false;
        },
        onDescriptionChange() {
            this.changes.description = this.$refs.toastuiEditor.invoke('getMarkdown');
        },
    },

};
</script>
